import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { RoleError, RoleGateway } from '../../../gateways/role.gateway';
import { Role } from '../../../models/role.model';

type DetailState = 'loading' | 'ready' | 'not-found' | 'error';

@Component({
    selector: 'app-role-detail',
    standalone: true,
    imports: [CommonModule, RouterLink],
    template: `
        <div class="page-content">
            @if (state() === 'loading') {
                <div class="state" role="status" aria-live="polite">Loading role…</div>
            }

            @if (state() === 'not-found') {
                <div class="state" role="alert">
                    <h1>Role not found</h1>
                    <p>This role does not exist or has been deleted.</p>
                    <a class="btn" [routerLink]="['../../roles']">Back to roles</a>
                </div>
            }

            @if (state() === 'error') {
                <div class="state state-error" role="alert">
                    <p>{{ errorMessage() }}</p>
                    <a class="btn" [routerLink]="['../../roles']">Back to roles</a>
                </div>
            }

            @if (state() === 'ready' && role(); as r) {
                <div class="page-header">
                    <div>
                        <h1>{{ r.name }}</h1>
                        <p>{{ r.description }}</p>
                    </div>
                    <div class="header-actions">
                        <a class="btn" [routerLink]="['../../roles', r.id, 'edit']">Edit</a>
                        <button
                            type="button"
                            class="btn btn-danger"
                            (click)="remove(r)"
                            [disabled]="deleting()"
                        >
                            {{ deleting() ? 'Deleting…' : 'Delete' }}
                        </button>
                    </div>
                </div>

                @if (rowError()) {
                    <div class="state state-error" role="alert">{{ rowError() }}</div>
                }

                <dl class="detail-grid">
                    <dt>ID</dt>
                    <dd>{{ r.id }}</dd>
                    <dt>Project ID</dt>
                    <dd>{{ r.projectId }}</dd>
                    <dt>Version</dt>
                    <dd>{{ r.version }}</dd>
                    <dt>Tools</dt>
                    <dd>
                        @if (r.toolIds.length) {
                            {{ r.toolIds.join(', ') }}
                        } @else {
                            No tools assigned.
                        }
                    </dd>
                    <dt>Instructions</dt>
                    <dd>
                        @if (r.instructions.length) {
                            <ul class="instructions">
                                @for (instruction of r.instructions; track $index) {
                                    <li>
                                        <strong>{{ instruction.type }}:</strong>
                                        {{ instruction.content }}
                                    </li>
                                }
                            </ul>
                        } @else {
                            No instructions.
                        }
                    </dd>
                </dl>

                <a class="btn" [routerLink]="['../../roles']">Back to roles</a>
            }
        </div>
    `,
    styles: [
        `
            .page-content {
                padding: 2rem;
            }
            .page-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 1rem;
                margin-bottom: 1.5rem;
            }
            .header-actions {
                display: flex;
                gap: 0.5rem;
                flex-shrink: 0;
            }
            h1 {
                font-size: 1.75rem;
                font-weight: 600;
                margin-bottom: 0.5rem;
                color: #1a1a1a;
            }
            p {
                color: #666;
                margin: 0;
            }
            .state {
                padding: 2rem;
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                text-align: center;
                color: #666;
            }
            .state-error {
                border-color: #f5c2c2;
                background: #fdecea;
                color: #a12622;
                text-align: left;
            }
            .detail-grid {
                display: grid;
                grid-template-columns: 160px 1fr;
                gap: 0.5rem 1rem;
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                padding: 1.5rem;
                margin-bottom: 1.5rem;
            }
            dt {
                font-weight: 600;
                color: #445;
            }
            dd {
                margin: 0;
                color: #1a1a1a;
                word-break: break-word;
            }
            .instructions {
                margin: 0;
                padding-left: 1.25rem;
            }
            .btn {
                display: inline-block;
                padding: 0.5rem 0.9rem;
                border-radius: 6px;
                border: 1px solid #d0d0d0;
                background: white;
                color: #1a1a1a;
                text-decoration: none;
                cursor: pointer;
                font-size: 0.9rem;
            }
            .btn:hover {
                background: #f5f5f5;
            }
            .btn-danger {
                border-color: #e0b4b4;
                color: #a12622;
            }
            .btn-danger:hover {
                background: #fdecea;
            }
            .btn:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
        `,
    ],
})
export class RoleDetail implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly gateway = inject(RoleGateway);

    readonly state = signal<DetailState>('loading');
    readonly role = signal<Role | null>(null);
    readonly errorMessage = signal('');
    readonly rowError = signal('');
    readonly deleting = signal(false);

    private projectId = '';
    private roleId = '';

    ngOnInit(): void {
        this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
        this.roleId = this.route.snapshot.paramMap.get('roleId') ?? '';
        this.load();
    }

    load(): void {
        this.state.set('loading');
        this.gateway.getRole(this.projectId, this.roleId).subscribe({
            next: (role) => {
                this.role.set(role);
                this.state.set('ready');
            },
            error: (err: Error) => {
                if (this.isNotFound(err)) {
                    this.state.set('not-found');
                } else {
                    this.errorMessage.set(err.message);
                    this.state.set('error');
                }
            },
        });
    }

    private isNotFound(err: Error): boolean {
        return err instanceof RoleError && (err.kind === 'not-found' || err.status === 404);
    }

    remove(role: Role): void {
        if (!confirm(`Delete "${role.name}"? This cannot be undone.`)) {
            return;
        }
        this.deleting.set(true);
        this.rowError.set('');
        this.gateway.deleteRole(this.projectId, role.id).subscribe({
            next: () => {
                this.router.navigate(['../../roles'], { relativeTo: this.route });
            },
            error: (err: Error) => {
                this.deleting.set(false);
                this.rowError.set(err.message);
            },
        });
    }
}
